import { useMutation } from '@tanstack/react-query';
import { useEffect, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';

import { AuthLayout } from '../components/layout';
import { Button, Field, Input, Segmented } from '../components/ui';
import { useI18n } from '../i18n';
import { LOCALES, type Locale } from '../i18n/types';
import { ApiError, api } from '../lib/api';
import { cn, passwordStrength } from '../lib/ui';

/** Translates an API error code, falling back to a generic message. */
function useErrorMessage() {
  const { t } = useI18n();
  return (error: unknown): string => {
    if (!(error instanceof ApiError)) return t('errors.generic');
    switch (error.code) {
      case 'invalid_credentials':
        return t('auth.invalidCredentials');
      case 'account_disabled':
        return t('auth.accountDisabled');
      case 'invitation_pending':
        return t('invite.invalid');
      case 'current_password_incorrect':
        return t('auth.currentPasswordWrong');
      case 'password_too_short':
        return t('auth.passwordTooShort');
      case 'email_taken':
        return t('users.emailTaken');
      case 'last_admin':
        return t('users.lastAdmin');
      case 'validation_failed':
        return t('errors.validation');
      case 'offline':
        return t('errors.offline');
      case 'file_too_large':
        return t('errors.fileTooLarge');
      case 'paperless_not_configured':
        return t('errors.paperlessNotConfigured');
      case 'paperless_unreachable':
        return t('paperless.unreachable');
      case 'paperless_unauthorized':
      case 'paperless_bad_credentials':
        return t('paperless.unauthorized');
      default:
        return t('errors.generic');
    }
  };
}

export { useErrorMessage };

function LanguagePicker({ value, onChange }: { value: Locale; onChange: (l: Locale) => void }) {
  return (
    <Segmented
      className="w-full"
      value={value}
      onChange={onChange}
      options={LOCALES.map((locale) => ({ value: locale.value, label: locale.nativeLabel }))}
    />
  );
}

/** Reassurance, not a gate — the only hard rule is length. */
function PasswordMeter({ password }: { password: string }) {
  const { t } = useI18n();
  if (!password) return null;

  const score = passwordStrength(password);
  const labels = [
    t('auth.strength.weak'),
    t('auth.strength.fair'),
    t('auth.strength.good'),
    t('auth.strength.strong'),
  ];
  const colors = ['var(--critical)', 'var(--caution)', 'var(--positive)', 'var(--positive)'];

  return (
    <div className="mt-2 flex items-center gap-2.5">
      <div className="flex flex-1 gap-1">
        {[0, 1, 2, 3].map((index) => (
          <span
            key={index}
            className="h-[3px] flex-1 rounded-full transition-colors"
            style={{ background: index <= score ? colors[score] : 'var(--line)' }}
          />
        ))}
      </div>
      <span className="text-[11px] text-[var(--ink-secondary)]">
        {t('auth.strength.label')}: {labels[score]}
      </span>
    </div>
  );
}

// --- Sign in ----------------------------------------------------------------

export function SignInScreen({ onSignedIn }: { onSignedIn: () => void }) {
  const { t } = useI18n();
  const toMessage = useErrorMessage();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [forgot, setForgot] = useState(false);

  const login = useMutation({
    mutationFn: () => api.login({ email, password }),
    onSuccess: onSignedIn,
  });

  const forgotPassword = useMutation({ mutationFn: () => api.forgotPassword({ email }) });

  if (forgot) {
    return (
      <AuthLayout title={t('auth.resetTitle')} subtitle={t('auth.resetSubtitle')}>
        {forgotPassword.isSuccess ? (
          <div className="text-center">
            <p className="text-[14px] leading-relaxed text-[var(--ink-secondary)]">
              {forgotPassword.data.emailConfigured ? t('auth.resetSent') : t('auth.resetNoEmail')}
            </p>
            <Button className="mt-5" fullWidth variant="secondary" onClick={() => setForgot(false)}>
              {t('common.back')}
            </Button>
          </div>
        ) : (
          <form
            className="space-y-4"
            onSubmit={(event) => {
              event.preventDefault();
              forgotPassword.mutate();
            }}
          >
            <Field label={t('auth.email')}>
              <Input
                type="email"
                autoComplete="email"
                required
                value={email}
                onChange={(event) => setEmail(event.target.value)}
              />
            </Field>
            <Button type="submit" fullWidth size="lg" loading={forgotPassword.isPending}>
              {t('common.next')}
            </Button>
            <Button type="button" variant="ghost" fullWidth onClick={() => setForgot(false)}>
              {t('common.cancel')}
            </Button>
          </form>
        )}
      </AuthLayout>
    );
  }

  return (
    <AuthLayout title={t('auth.welcomeBack')} subtitle={t('auth.signInSubtitle')}>
      <form
        className="space-y-4"
        onSubmit={(event) => {
          event.preventDefault();
          login.mutate();
        }}
      >
        <Field label={t('auth.email')}>
          <Input
            type="email"
            autoComplete="email"
            autoCapitalize="none"
            required
            value={email}
            onChange={(event) => setEmail(event.target.value)}
          />
        </Field>

        <Field label={t('auth.password')}>
          <Input
            type="password"
            autoComplete="current-password"
            required
            value={password}
            onChange={(event) => setPassword(event.target.value)}
          />
        </Field>

        {login.isError ? (
          <p className="text-[13px] text-[var(--critical)]">{toMessage(login.error)}</p>
        ) : null}

        <Button type="submit" fullWidth size="lg" loading={login.isPending}>
          {login.isPending ? t('auth.signingIn') : t('auth.signIn')}
        </Button>

        <button
          type="button"
          onClick={() => setForgot(true)}
          className="block w-full pt-1 text-center text-[13px] text-[var(--ink-secondary)] hover:text-[var(--ink)]"
        >
          {t('auth.forgotPassword')}
        </button>
      </form>
    </AuthLayout>
  );
}

// --- First run --------------------------------------------------------------

export function SetupScreen({
  locale,
  onLocaleChange,
  onComplete,
}: {
  locale: Locale;
  onLocaleChange: (locale: Locale) => void;
  onComplete: () => void;
}) {
  const { t } = useI18n();
  const toMessage = useErrorMessage();
  const [form, setForm] = useState({
    workspaceName: '',
    ledgerName: '',
    name: '',
    email: '',
    password: '',
  });

  const setup = useMutation({
    mutationFn: () => api.setup({ ...form, locale }),
    onSuccess: onComplete,
  });

  const tooShort = form.password.length > 0 && form.password.length < 10;

  return (
    <AuthLayout title={t('setup.title')} subtitle={t('setup.subtitle')}>
      <form
        className="space-y-4"
        onSubmit={(event) => {
          event.preventDefault();
          if (tooShort) return;
          setup.mutate();
        }}
      >
        <Field label={t('setup.languageQuestion')}>
          <LanguagePicker value={locale} onChange={onLocaleChange} />
        </Field>

        <Field label={t('setup.workspaceName')} hint={t('setup.workspaceHint')} optional>
          <Input
            value={form.workspaceName}
            placeholder={t('setup.workspacePlaceholder')}
            onChange={(event) => setForm({ ...form, workspaceName: event.target.value })}
          />
        </Field>

        <Field label={t('setup.firstLedger')} hint={t('setup.firstLedgerHint')} optional>
          <Input
            value={form.ledgerName}
            placeholder={t('ledger.namePlaceholder')}
            onChange={(event) => setForm({ ...form, ledgerName: event.target.value })}
          />
        </Field>

        <Field label={t('setup.yourName')}>
          <Input
            required
            autoComplete="name"
            value={form.name}
            onChange={(event) => setForm({ ...form, name: event.target.value })}
          />
        </Field>

        <Field label={t('auth.email')}>
          <Input
            type="email"
            required
            autoComplete="email"
            autoCapitalize="none"
            value={form.email}
            onChange={(event) => setForm({ ...form, email: event.target.value })}
          />
        </Field>

        <Field label={t('auth.password')} error={tooShort ? t('auth.passwordTooShort') : undefined}>
          <Input
            type="password"
            required
            minLength={10}
            autoComplete="new-password"
            value={form.password}
            onChange={(event) => setForm({ ...form, password: event.target.value })}
          />
          <PasswordMeter password={form.password} />
        </Field>

        {setup.isError ? (
          <p className="text-[13px] text-[var(--critical)]">{toMessage(setup.error)}</p>
        ) : null}

        <Button type="submit" fullWidth size="lg" loading={setup.isPending}>
          {setup.isPending ? t('setup.creating') : t('setup.createAccount')}
        </Button>
      </form>
    </AuthLayout>
  );
}

// --- Invitation -------------------------------------------------------------

export function AcceptInviteScreen({
  locale,
  onLocaleChange,
  onComplete,
}: {
  locale: Locale;
  onLocaleChange: (locale: Locale) => void;
  onComplete: () => void;
}) {
  const { t } = useI18n();
  const toMessage = useErrorMessage();
  const { token = '' } = useParams();
  const [invitation, setInvitation] = useState<{ name: string; workspaceName: string } | null>(
    null,
  );
  const [status, setStatus] = useState<'loading' | 'ready' | 'invalid'>('loading');
  const [name, setName] = useState('');
  const [password, setPassword] = useState('');

  useEffect(() => {
    let canceled = false;
    api
      .invitation(token)
      .then((data) => {
        if (canceled) return;
        setInvitation(data);
        setName(data.name);
        // Start the new person in whatever language the household uses.
        onLocaleChange(data.defaultLocale);
        setStatus('ready');
      })
      .catch(() => {
        if (!canceled) setStatus('invalid');
      });
    return () => {
      canceled = true;
    };
    // The token is the only thing this depends on; re-running on locale change
    // would fight the user's own choice below.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  const accept = useMutation({
    mutationFn: () => api.acceptInvitation(token, { password, name, locale }),
    onSuccess: onComplete,
  });

  if (status === 'loading') {
    return <AuthLayout title={t('common.loading')}>{null}</AuthLayout>;
  }

  if (status === 'invalid') {
    return (
      <AuthLayout title={t('invite.invalid')} subtitle={t('invite.invalidHelp')}>
        <Link to="/" className="block">
          <Button fullWidth variant="secondary">
            {t('auth.signIn')}
          </Button>
        </Link>
      </AuthLayout>
    );
  }

  const tooShort = password.length > 0 && password.length < 10;

  return (
    <AuthLayout
      title={
        invitation?.workspaceName
          ? t('invite.title', { workspace: invitation.workspaceName })
          : t('invite.titleGeneric')
      }
      subtitle={t('invite.subtitle')}
      footer={
        <Link to="/" className="text-[13px] text-[var(--ink-secondary)] hover:text-[var(--ink)]">
          {t('invite.signInInstead')}
        </Link>
      }
    >
      <form
        className="space-y-4"
        onSubmit={(event) => {
          event.preventDefault();
          if (tooShort) return;
          accept.mutate();
        }}
      >
        <Field label={t('settings.language')}>
          <LanguagePicker value={locale} onChange={onLocaleChange} />
        </Field>

        <Field label={t('invite.yourName')}>
          <Input required value={name} onChange={(event) => setName(event.target.value)} />
        </Field>

        <Field label={t('auth.password')} error={tooShort ? t('auth.passwordTooShort') : undefined}>
          <Input
            type="password"
            required
            minLength={10}
            autoComplete="new-password"
            value={password}
            onChange={(event) => setPassword(event.target.value)}
          />
          <PasswordMeter password={password} />
        </Field>

        {accept.isError ? (
          <p className="text-[13px] text-[var(--critical)]">{toMessage(accept.error)}</p>
        ) : null}

        <Button type="submit" fullWidth size="lg" loading={accept.isPending}>
          {t('invite.accept')}
        </Button>
      </form>
    </AuthLayout>
  );
}

// --- Password reset ---------------------------------------------------------

export function ResetPasswordScreen() {
  const { t } = useI18n();
  const navigate = useNavigate();
  const { token = '' } = useParams();
  const [password, setPassword] = useState('');
  const [valid, setValid] = useState<boolean | null>(null);

  useEffect(() => {
    api
      .checkReset(token)
      .then((result) => setValid(result.valid))
      .catch(() => setValid(false));
  }, [token]);

  const reset = useMutation({
    mutationFn: () => api.resetPassword(token, password),
  });

  if (valid === false) {
    return (
      <AuthLayout title={t('auth.resetInvalid')}>
        <Button fullWidth variant="secondary" onClick={() => navigate('/')}>
          {t('auth.signIn')}
        </Button>
      </AuthLayout>
    );
  }

  if (reset.isSuccess) {
    return (
      <AuthLayout title={t('auth.resetDone')}>
        <Button fullWidth onClick={() => navigate('/')}>
          {t('auth.signIn')}
        </Button>
      </AuthLayout>
    );
  }

  const tooShort = password.length > 0 && password.length < 10;

  return (
    <AuthLayout title={t('auth.resetTitle')} subtitle={t('auth.resetSubtitle')}>
      <form
        className="space-y-4"
        onSubmit={(event) => {
          event.preventDefault();
          if (tooShort) return;
          reset.mutate();
        }}
      >
        <Field
          label={t('auth.newPassword')}
          error={tooShort ? t('auth.passwordTooShort') : undefined}
        >
          <Input
            type="password"
            required
            minLength={10}
            autoComplete="new-password"
            value={password}
            onChange={(event) => setPassword(event.target.value)}
          />
          <PasswordMeter password={password} />
        </Field>

        <Button
          type="submit"
          fullWidth
          size="lg"
          loading={reset.isPending}
          className={cn(valid === null && 'pointer-events-none opacity-60')}
        >
          {t('common.save')}
        </Button>
      </form>
    </AuthLayout>
  );
}
